import React from 'react';
import { observer } from 'mobx-react';

import { Label, Button, Input, TextArea, Select } from 'common/materials';

import PrivacyConfigViewModel, { PrivacyConfigForm } from './PrivacyConfig.viewmodel';

type NoticeKey = Exclude<keyof PrivacyConfigForm, 'script_banner' | 'is_active'>;

const NOTICE_FIELDS: { key: NoticeKey; label: string; placeholder: string }[] = [
    {
        key: 'privacy_notice_en',
        label: 'Privacy Notice (EN)',
        placeholder: 'https://www.example.com/en/privacy-notice'
    },
    {
        key: 'cookie_notice_en',
        label: 'Cookie Notice (EN)',
        placeholder: 'https://www.example.com/en/cookies-policy'
    },
    {
        key: 'privacy_notice_th',
        label: 'นโยบายคุ้มครองข้อมูลส่วนบุคคล (TH)',
        placeholder: 'https://www.example.com/th/privacy-notice'
    },
    {
        key: 'cookie_notice_th',
        label: 'นโยบายการใช้คุกกี้ (TH)',
        placeholder: 'https://www.example.com/th/cookies-policy'
    }
];

function PrivacyConfig() {
    const {
        userRole,
        companyOptions,
        selectedCompany,
        isCompanyLocked,
        isNewConfig,
        existingConfig,
        form,
        handleSelectCompany,
        handleChange,
        handleReset,
        handleSave
    } = PrivacyConfigViewModel();

    if (userRole !== 'Super Admin' && userRole !== 'Admin') {
        return (
            <React.Fragment>
                <p className="text-2xl font-bold">Privacy Config</p>
                <p className="p-4 text-neutral-500">You don't have permission to access this page.</p>
            </React.Fragment>
        );
    }

    return (
        <React.Fragment>
            <p className="text-2xl font-bold">Privacy Config</p>
            <div className="flex flex-col gap-4 p-4">
                <div className="grid grid-cols-1 lg:grid-cols-2">
                    <div className="flex flex-col gap-2">
                        <Label>Company</Label>
                        <Select
                            isDisabled={isCompanyLocked}
                            placeholder="กรุณาเลือก Company"
                            options={companyOptions}
                            getOptionLabel={company => company.company_name}
                            getOptionValue={company => company.sid}
                            value={selectedCompany ?? null}
                            onChange={handleSelectCompany}
                            className="w-full"
                        />
                    </div>
                </div>

                {selectedCompany && (
                    <React.Fragment>
                        <div className="flex flex-wrap items-center gap-4">
                            <label className="inline-flex cursor-pointer items-center gap-2 text-sm">
                                <input
                                    type="checkbox"
                                    className="h-4 w-4 accent-primary-900"
                                    checked={form.is_active}
                                    onChange={event =>
                                        handleChange('is_active', event.target.checked)
                                    }
                                />
                                Active (show banner and footer for this company)
                            </label>
                            {isNewConfig && (
                                <span className="text-sm text-neutral-500">
                                    No config yet - saving will create a new one.
                                </span>
                            )}
                            {existingConfig?.updated_on && (
                                <span className="text-sm text-neutral-500">
                                    Last updated {existingConfig.updated_on}
                                    {existingConfig.updated_by && ` by ${existingConfig.updated_by}`}
                                </span>
                            )}
                        </div>

                        <div className="flex flex-col gap-2">
                            <Label>Script Banner</Label>
                            <TextArea
                                rows={5}
                                className="font-mono text-xs"
                                placeholder={
                                    '<script type="text/javascript" src="https://.../reddot.js"></script>\n<link rel="stylesheet" href="https://.../style.css">'
                                }
                                value={form.script_banner}
                                onChange={event => handleChange('script_banner', event.target.value)}
                            />
                            <span className="text-xs text-neutral-500">
                                A script URL or HTML tags (&lt;script&gt;, &lt;link&gt;, &lt;style&gt;). It runs on
                                every page for users of this company.
                            </span>
                        </div>

                        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                            {NOTICE_FIELDS.map(field => (
                                <div
                                    key={field.key}
                                    className="flex flex-col gap-2"
                                >
                                    <Label>{field.label}</Label>
                                    <Input
                                        type="url"
                                        placeholder={field.placeholder}
                                        value={form[field.key]}
                                        onChange={event => handleChange(field.key, event.target.value)}
                                    />
                                </div>
                            ))}
                        </div>

                        <div className="flex justify-end gap-2">
                            <Button
                                outline
                                onClick={handleReset}
                            >
                                Reset
                            </Button>
                            <Button
                                className="bg-primary-900 text-white"
                                onClick={handleSave}
                            >
                                Save
                            </Button>
                        </div>
                    </React.Fragment>
                )}
            </div>
        </React.Fragment>
    );
}

export default observer(PrivacyConfig);
